// Priviledge provider to handle priviledge checking and state management to view and hide components based on user priviledges

import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AuthState } from "../../redux/authSlice";
import { RootState } from "../../redux/store";
import { JWTDecoded, Permissions } from "../../type/jwt-type";

export type permissionsGroup = "user" | "companies" | "roles" | "dashboard" | "commercial_network" | "statuses" | "statuses_types" | "contracts" | "expirations" |
  "invoices" | "renovations" | "clients" | "students" | "products" | "corporation" | "document_type" | "departments" | "tutors" | "consultancies" |
  "campaigns" | "studies" | "professional_categories" | "quotation_groups" | "complements" | "contract_groups" | "incidence_types" | "incidences" | "orders" | "order_types" |
  "events" | "group_codes" |
  "rankings" | "product_types" | "leads" | "calls" | "ko_reasons" | "inspector_credentials" | "order_campaigns" | "call_result_classifications" |
  "call_classifications" | "communications" | "platform_advances";

type PrivilegeContextType = {
  permissions?: Permissions | null,
  roles?: string[],
  isFacilitator: boolean,
  isFacilitatorManager: boolean,
  userCan: (action: string, group: permissionsGroup, superAdmin?: boolean) => boolean,
}

// Create default context
const PrivilegeContext: React.Context<PrivilegeContextType> = React.createContext<PrivilegeContextType>({
  permissions: {},
  roles: [],
  isFacilitator: false,
  isFacilitatorManager: false,
  userCan: (action: string, group: permissionsGroup, superAdmin?: boolean): boolean => false,
});

type PrivilegeProviderProps = {
  children: React.ReactNode
}

const decode = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  return JSON.parse(window.atob(base64));
}

// Create provider component
const PrivilegeProvider: React.FC<PrivilegeProviderProps> = ({ children }) => {
  const { user }: AuthState = useSelector((state: RootState) => state.auth);
  const [permissions, setPermissions] = useState<Permissions | undefined | null>(undefined);
  const [roles, setRoles] = useState<string[] | undefined>([]);
  const [isFacilitator, setIsFacilitator] = useState<boolean>(false);
  const [isFacilitatorManager, setIsFacilitatorManager] = useState<boolean>(false);

  const resetState = () => {
    setPermissions(undefined);
    setRoles(undefined);
    setIsFacilitator(false);
    setIsFacilitatorManager(false);
  }

  const decodeToken = async () => {
    if (user !== null) {
      const decoded: JWTDecoded = decode(user?.token as string);
      setRoles(decoded.roles)
      setPermissions(decoded.permissions);
      setIsFacilitator(Boolean(decoded.isFacilitator));
      setIsFacilitatorManager(Boolean(decoded.isFacilitatorManager));
    }
  }

  // JWT token decode and configure state
  useEffect(() => {
    resetState();
    if (user) {
      decodeToken();
    } else {
      setPermissions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const userCan = (action: string, group: permissionsGroup, superAdmin?: boolean) => {
    if (permissions === undefined || permissions === null) {
      return false;
    }

    if (superAdmin && !roles?.includes("Superadministrador")) {
      return false;
    }

    if (permissions[group] && permissions[group].includes(action)) {
      return true;
    }

    return false;
  }

  // Define context and mount
  const value: PrivilegeContextType = {
    permissions: permissions,
    roles: roles,
    isFacilitator,
    isFacilitatorManager,
    userCan
  }

  return (
    <>
      {permissions !== undefined
        ? <PrivilegeContext.Provider value={value}>{children}</PrivilegeContext.Provider>
        : <></>
      }
    </>
  )
}

export { PrivilegeContext, PrivilegeProvider };

export function usePrivilege() {
  return useContext(PrivilegeContext);
}
